import React from 'react';
import { makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Theme } from '@material-ui/core';
import { StyledText } from './StyledText';
import { Projection } from '../types/types';
import { formatDollarsAsMillions } from '../lib/utils';
import DollarInput from './DollarInput';


const useInputStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '150px',
    border: '1px solid rgb(208, 214, 218)',
    borderRadius: 5,
    padding: 5,
    fontFamily: theme.typography.fontFamily,
    fontSize: 16,
    fontWeight: 300,
    '&::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      '-moz-appearance': 'none',
      margin: 0
    },
    textAlign: 'right',
    '&[type=number]': {
      '-moz-appearance': 'textfield',
      '-webkit-appearance': 'textfield'
    },
    '&:disabled': {
      width: '100%',
      border: 'none',
      textAlign: 'right',
      backgroundColor: 'inherit',
      color: theme.palette.text.primary,
      opacity: 1 /* required on iOS */
    }
  }
}));

const NumberInput = ({ id, value, editable }: { id: string, value: number, editable: boolean }) => {
  const inputStyles = useInputStyles();
  const getVal = () => (value ? formatDollarsAsMillions(value, true) : '_');

  return editable ? (
    <DollarInput
      id={id}
      placeholder=''
      marginTop='0px'
      allowNegative
    />
  )
    : (
      <input
        type='text'
        id={id}
        name={id}
        value={getVal()}
        disabled
        className={inputStyles.root}
      />
    );
};

const useTableStyles = makeStyles((theme: Theme) => ({
  root: {
    '& .MuiTableHead-root > .MuiTableRow-root': {
      backgroundColor: theme.palette.primary.light
    },
    '& .MuiTableCell-root': {
      padding: 5
    }
  }
}));

interface FinanceTableProps {
  projections: Projection[]
  editable: boolean
  vertical: boolean
}

const FinanceTable: React.FC<FinanceTableProps> = ({
  projections, vertical = false, editable = false
}) => {
  const tableStyles = useTableStyles();

  const VerticalHeaderRow = (
    <TableRow>
      <TableCell><StyledText>{' '}</StyledText></TableCell>
      <TableCell align='center'>
        <StyledText variant='body2' color='primary'>Revenue</StyledText>
      </TableCell>
      <TableCell align='center'>
        <StyledText variant='body2' color='primary'>EBITDA</StyledText>
      </TableCell>
    </TableRow>
  );

  const HorizontalHeaderRow = (
    <TableRow>
      <TableCell><StyledText>{' '}</StyledText></TableCell>
      {projections.map((proj, i) => (
        <TableCell align='right' key={i}>
          <StyledText variant='body2' color='primary'>
            {proj.year}
          </StyledText>
        </TableCell>
      ))}
      {vertical}
    </TableRow>
  );

  const VerticalBodyRows = (
    <>
      {projections.map((proj, i) => (
        <TableRow key={i}>
          <TableCell align='center'>
            <StyledText variant='body2' color='primary' style={{ height: '100%' }}>
              {proj.year}
            </StyledText>
          </TableCell>
          <TableCell align='center'>
            <NumberInput
              id={`financialProjection[${i}].revenue`}
              value={proj.revenue}
              editable={editable}
            />
          </TableCell>
          <TableCell align='center'>
            <NumberInput
              id={`financialProjection[${i}].EBITDA`}
              value={proj.EBITDA}
              editable={editable}
            />
          </TableCell>
        </TableRow>
      ))}
    </>
  );

  const HorizontalBodyRows = (
    <>
      <TableRow>
        <TableCell>
          <StyledText variant='body2' color='primary'>Revenue</StyledText>
        </TableCell>
        {projections.map((proj, i) => (
          <TableCell key={i}>
            <NumberInput
              id={`financialProjection[${i}].revenue`}
              value={proj.revenue}
              editable={editable}
            />
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        <TableCell>
          <StyledText variant='body2' color='primary'>EBITDA</StyledText>
        </TableCell>
        {projections.map((proj, i) => (
          <TableCell key={i}>
            <NumberInput
              id={`financialProjection[${i}].EBITDA`}
              value={proj.EBITDA}
              editable={editable}
            />
          </TableCell>
        ))}
      </TableRow>
    </>
  );

  return (
    <TableContainer classes={tableStyles}>
      <Table>
        <TableHead>
          {vertical ? VerticalHeaderRow : HorizontalHeaderRow}
        </TableHead>
        <TableBody>
          {vertical ? VerticalBodyRows : HorizontalBodyRows}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FinanceTable;
